import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import type { SessionUser, UserRole } from '../types'

interface AuthContextValue {
  user: SessionUser | null
  loading: boolean
  csrf: string
  login: (identifier: string, password: string, role: UserRole) => Promise<void>
  previewLogin: (role: UserRole) => void
  logout: () => Promise<void>
  refresh: () => Promise<void>
  previewEnabled: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)
const previewEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_PREVIEW_LOGIN === 'true'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [csrf, setCsrf] = useState('')
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include', headers: { Accept: 'application/json' } })
      if (!response.ok) {
        setUser(null)
        setCsrf('')
        return
      }
      const body = await response.json() as { user: SessionUser; csrf?: string }
      setUser(body.user)
      setCsrf(body.csrf || '')
    } catch {
      setUser(null)
      setCsrf('')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    const loadSession = async () => {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'include', headers: { Accept: 'application/json' } })
        if (!active) return
        if (!response.ok) {
          setUser(null)
          setCsrf('')
          return
        }
        const body = await response.json() as { user: SessionUser; csrf?: string }
        if (!active) return
        setUser(body.user)
        setCsrf(body.csrf || '')
      } catch {
        if (!active) return
        setUser(null)
        setCsrf('')
      } finally {
        if (active) setLoading(false)
      }
    }

    void loadSession()
    return () => { active = false }
  }, [])

  const login = async (identifier: string, password: string, role: UserRole) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ identifier, password, portal: role }),
    })
    const body = await response.json().catch(() => ({})) as { error?: string; user?: SessionUser; csrf?: string }
    if (!response.ok || !body.user) throw new Error(body.error || '로그인 정보를 확인해 주세요.')
    setUser(body.user)
    setCsrf(body.csrf || '')
  }

  const previewLogin = (role: UserRole) => {
    if (!previewEnabled) return
    setUser({
      id: `preview-${role}`,
      loginId: `preview-${role.replace('_', '-')}`,
      email: 'preview@local.invalid',
      emailVerified: true,
      name: role === 'super_admin' ? '최고 관리자' : role === 'hospital_admin' ? '병원 담당자' : '보호자',
      roles: role === 'super_admin' ? ['super_admin', 'member'] : [role],
      organization: role === 'hospital_admin' ? '로컬 미리보기 병원' : 'Animal Care',
    })
    setCsrf('preview-csrf')
    setLoading(false)
  }

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include', headers: csrf ? { 'X-CSRF-Token': csrf } : undefined }).catch(() => undefined)
    setUser(null)
    setCsrf('')
  }

  const value = { user, loading, csrf, login, previewLogin, logout, refresh, previewEnabled }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
